import React, { useState } from "react";
import { motion } from "framer-motion";
import { Maximize2, Play } from "lucide-react";
import { YOUTUBE_THUMBNAILS, driveThumb } from "@/lib/lumiqoData";
import MediaLightbox from "@/components/lumiqo/MediaLightbox";

const YouTubeThumbnails = () => {
  const [open, setOpen] = useState(false);
  const [media, setMedia] = useState(null);

  const handleOpen = (thumb) => {
    setMedia({
      kind: "image",
      driveId: thumb.driveId,
      title: thumb.title,
      aspect: "16/9",
    });
    setOpen(true);
  };

  return (
    <section
      id="youtube-thumbnails"
      className="relative py-24 sm:py-32 bg-white"
      data-testid="yt-thumbnails-section"
    >
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1d72ff]/10 text-[#1d72ff] text-xs font-semibold tracking-wide uppercase">
              <Play className="w-3 h-3 fill-current" />
              YouTube Thumbnails
            </div>
            <h2
              className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#0a1d4d] mt-5 leading-[1.05]"
              data-testid="yt-thumbnails-heading"
            >
              Thumbnails that <br />
              <span className="lq-text-gradient">earn the click</span>.
            </h2>
          </div>
          <p className="text-[#0a1d4d]/65 max-w-md text-base">
            Custom thumbnail design tuned to your channel’s voice — built to
            beat your own best CTR.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {YOUTUBE_THUMBNAILS.map((thumb, i) => (
            <motion.button
              key={thumb.driveId}
              type="button"
              onClick={() => handleOpen(thumb)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.07 }}
              className="group relative aspect-video rounded-2xl sm:rounded-3xl overflow-hidden bg-[#0a1d4d] border border-[#0a1d4d]/10 lq-shadow-soft hover:lq-shadow-glow transition-all duration-300"
              data-testid={`yt-thumbnail-${i}`}
            >
              <img
                src={driveThumb(thumb.driveId, 1200)}
                alt={thumb.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-14 h-14 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-xl">
                  <Maximize2 className="w-5 h-5 text-[#0a1d4d]" />
                </div>
              </div>
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                <span className="text-white text-xs font-display font-semibold tracking-tight">
                  {thumb.title}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-white/90 text-[#0a1d4d] text-[10px] font-bold uppercase tracking-wide">
                  16:9
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <MediaLightbox open={open} onOpenChange={setOpen} media={media} />
    </section>
  );
};

export default YouTubeThumbnails;
